/**
 * EmptyEventsFallback Component
 *
 * Home overview's "no upcoming events" card. Role/state-aware per #3059: it
 * shows the same diagnosis and casing as the Activity events tab, and never
 * renders a primary CTA the API would refuse. Copy comes from the shared
 * events empty-state resolver so web and the native port (#3064) stay in sync.
 *
 * The Home overview has no in-place past-events filter, so `supportsPastFilter`
 * is false: it never offers a "See past events" secondary that would dead-end.
 */

import Button from './Button';
import { useNavigate } from 'react-router-dom';
import { Calendar } from 'lucide-react';
import { resolveEventsEmptyState } from '../lib/eventsEmptyState';

interface EmptyEventsFallbackProps {
  /** Viewer leads or owns >= 1 club (the backend create-event gate). */
  isLeaderOfAnyClub: boolean;
  /** Viewer is a member of >= 1 club. */
  hasClubs: boolean;
  /** Any past events exist across the viewer's clubs. */
  hasPastEvents: boolean;
}

export function EmptyEventsFallback({
  isLeaderOfAnyClub,
  hasClubs,
  hasPastEvents,
}: EmptyEventsFallbackProps) {
  const navigate = useNavigate();

  const emptyState = resolveEventsEmptyState({
    isLeaderOfAnyClub,
    hasClubs,
    hasPastEvents,
    supportsPastFilter: false,
  });

  const onPrimary = () => {
    if (emptyState.primary?.action === 'create-event') {
      navigate('/events/create');
    } else {
      navigate('/clubs');
    }
  };

  return (
    <div className="bg-gradient-to-br from-oro-surface to-oro-surface-alt border border-oro-border rounded-xl p-8 shadow-card text-center">
      {/* Icon */}
      <div className="mb-6 flex justify-center">
        <div className="w-24 h-24 rounded-full bg-oro-surface-alt border-2 border-oro-border flex items-center justify-center">
          <Calendar className="w-12 h-12 text-text-muted" />
        </div>
      </div>

      {/* Title */}
      <h3 className="text-xl font-semibold text-text-primary mb-3">
        {emptyState.title}
      </h3>

      {/* Description */}
      <p className="text-text-secondary mb-6 max-w-md mx-auto leading-relaxed">
        {emptyState.message}
      </p>

      {/* Primary CTA (only when there's a non-refused move) */}
      {emptyState.primary && (
        <Button onClick={onPrimary} variant="primary" className="min-w-[140px]">
          {emptyState.primary.label}
        </Button>
      )}
    </div>
  );
}
